// GROUND REF — the CPU REFERENCE of the device ground painter (APP_GENSURF
// §5e, gs-gpupainter): one GmTex slot (j, tx, ty) = 512 px RGBA8 sRGB of
// albedo x E / W over 128 * 2^j m, painted from the DEVICE's inputs only.
// Plain JS, PER TEXEL, so groundgen.js's GLSL follows it line by line: every
// numpy raster op of the oracle (pipeline/surfacegen.py SurfacePainter in
// GENOME mode + texpaint.relight of bake_light's E / W) is a bounded loop
// over the cells / texels one texel reads, with the numpy's float32 where it
// decides (Math.fround) and its uint32 hashes (groundways.js gnoise). It is
// the pre-painted ground's painter (engine/steps/prepaint_slot, APP_PREPAINT);
// the gate is scripts/check-prepaint.mjs.
//
// API
//   prepWorld(inp) -> world   (once per world: the caches below)
//     inp = { w, h          world size, m (the 1 m class map's W x H; NW corner (0, 0), x east, z south)
//             cm            Uint8Array w h: class ids (row 0 north), classes.json
//             cmWin         {x0, z0, w, h} | undefined: cm (and sheet) hold only these cells of the w x h map —
//                           a window every read of the painted slots lies in (the pre-paint, APP_PREPAINT)
//             ids           {class key: id}, waterId, noneId
//             bands         region.bands (the apron's zones, treeline_m)
//             pal           painterpal.json (surfacegen PALETTE + MEASURED_KEYS; the gate pins it)
//             genome        genome.json (C4, ver 1); gtTable: pipeline/genome_noise.json (genometex.js)
//             residual      {codes Uint8Array hc wc 3, w, h, cell, x0, z0} (C5 codes, row 0 north)
//             s2col         {img Uint8Array hs ws 3, w, h, cell, x0, z0} | null (0 0 0 = none)
//             anchor, grassSat   meta.tex.s2.anchor, region.texture.grass_sat
//             sheet         Uint8Array w h | null: the sheet-lake bed mask (WATER-G, 1 = bed)
//             ways          groundways.js prepWays state | null
//             ground        Map 'k_tx_ty' -> ground-tree node {level, tx, ty, x, z, y, idx, lab}
//                           (node-local m, groundm/codec.js meshOf): the DRAWN heights
//             shd2          {vis: [{cell, w, h, a: Uint8Array, win?: {x0, z0, w, h}}], sky: [...]} decoded levels
//                           (win: a holds only those cells of the w x h level)
//             crowns        {trees: [decodeCrowns tiles], shrubs: [...], appearHist (meta.trees,
//                           255 counts), species (region vegetation.species), treelineM}
//             light         {sunAltDeg, sunAzDeg, sky, ground, sun, white} (meta.tex.light + region sun)
//             rockfield     {meta (meta.surface.rockfield, C7), img Uint8Array h w 3} | null
//             strata        ground.strata (C9 regen, 0..1): the bedding normal's weight
//             exemplars     {key: {img Uint8Array n n 3 sRGB (row 0 north), n, pxM}} the genome's
//                           exemplars (C4 surface/genome/<key>.webp decoded) | null
//             tiling        ground.tiling (C9 regen): TILE_KEYS with an exemplar paint its tiling
//             look          wayslook.json (default: ways.look): SEALED-MAT's kind colours }
//   paintSlot(world, j, tx, ty, opts) -> Uint8Array 512 512 4 RGBA8 sRGB, row 0 = the tile's
//       SOUTH edge (z max): GmTex's orientation — u = (x - tx m) / m, v = 1 - (z - ty m) / m
//   paintRect(world, j, x0, z0, wM, hM, pw, ph, opts) -> stages (row 0 = NORTH, pw ph):
//       {rgba (as paintSlot), albedo Float32Array 3 (sRGB 0..255 = paint()), E Uint8Array 3
//       (E / W sRGB-8, the relight's light8), lit Uint8Array 3, fsun / fsky Float32Array (the crown
//       factors at the texel), strata Float32Array (|the bedding tilt| at the texel, 0 = none),
//       elev, texel}
//   opts: { noResidual (R = 1), noCrowns (fsun = fsky = 1), noS2 (no measured S2 grass colour:
//           the class mean x R), noStrata (the flat triangle normal on rock), noTiling (the genome T on the tiled
//           classes), noSealedWin (the no-ways levels blend a sealed texel with its host) — the gate's red twins;
//           heights: 'tin' (default, the drawn ground) | 'dtm' (inp.dtm 1 m bilinear + numpy's
//           gradient: the ORACLE's heights, to prove the painter apart from the TIN);
//           crownCell (m, default 0.5 at texel <= 1 m, else the texel) }
//
// STAGES (the GLSL passes; per tile CPU uniforms: x0 z0 texel m r, the crown window, the SHD2
// level pick, the ways tile (groundways waysTile))
//   0. heights   the slot's ground-tree nodes (level j, the slot's footprint + the 3 x 3
//                neighbours the halo / crown window reach) rasterised top-down at the texel
//                centres (barycentric; the triangle under centre + (TIN_EX, TIN_EZ) samples, no
//                tie on the RTIN's diagonals; first node wins on a shared border): height + the FLAT
//                triangle gradient (gx, gz) -> elev, slope = |g| (class_mean, rock), the
//                Lambert normal. Texels past every node (the world's edge): the nearest covered
//                texel of the row, then of the column (the oracle's clamped bilinear).
//   1. albedo    paint()'s HALOED raster (m = haloPx(texel)): per texel the class weights of
//                the 2 m box blend (r = max(1, round(2 / texel)) texels, clipped to the raster)
//                = the exact area coverage of the 1 m class cells under the box of half-width
//                (r + 0.5) texel (texel < 1; >= 1: the box's texels' nearest cells), classes in
//                id order; per class: genome (T x class_mean x R x anchor, s2col on
//                MEASURED_KEYS; T = the exemplar tiling on TILE_KEYS with an exemplar, tileTexel), the hand palette (no record), water deep / the sheet bed (the
//                gravel's paint); out / sum w.
//                SEALED-MAT (road-keeps-colour): a sealed class (SEALED_MAT: asphalt, paved,
//                ballast) paints its way kind's flat look colour (the ways pass's built-way
//                interior, groundways M) at every level — no genome, residual or S2. A level
//                past the 1 m field (j > WAYS_MAX_J: geomOf gives it the 4 m mass, no 1 m window)
//                paints a texel whose own nearest class is sealed with that class alone
//                (SEALED-WIN): the host blend never dilutes a road, a plaza or a platform there.
//   2. ways      groundways.js waysPassRaster (host pass + its box means, then path + marks) at
//                EVERY level: j <= WAYS_MAX_J over the 1 m field, past it over the 4 m mass
//                (WAYS-Q4: every way kind at its mass, no sub-texel feature).
//   3. apron     past the class map: the elevation zones, blended by the 6 m box of
//                "outside" texels.
//   4. crowns    a WORLD-aligned crown grid (crownCell; window = the slot + the sun reach
//                LIGHT_REACH_MAX_M-capped + 3 cells, clipped to the world): the crown / shrub
//                discs (bake.DrawnCasters: profile cone / dome, base, opacity tiers, forms,
//                silhouette; y = the drawn ground at the crown) as the tallest top + tier per
//                cell, height over the drawn ground; the sun march (bake osteps, LIGHT_EPS_M:
//                the most opaque CROWN on the ray — terrain + opaque casters are SHD2's), its
//                1-cell box (edge-clamped at the world's edge, as bake._box_blur) -> vo; canopy c = clip((obj - 2) / 3) x 0.5 on crown cells; the sky
//                box occupancy over LIGHT_SKY_FINE_M (vis = 1 - f T^2 / (1 + T^2)) -> ov.
//                fsun = vo + (1 - vo) c, fsky = ov + (1 - ov) c (the bake's sky + (sky_t -
//                sky) c with sky_t := the SHD2 sky: the device has no terrain-only sky).
//                At the texel: bilinear (texel < cell) or the mean of its cells (>= cell).
//   5. light     E = light_irradiance(lam(n) vis fsun, sky fsky, n_y) (bake.light_irradiance,
//                meta.tex.light), vis / sky = SHD2 bilinear at the coarsest level whose cell
//                <= the texel (else the finest), water flat; E -> sRGB-8 (bake_light's own
//                encoding) -> lit = sRGB8(lin(albedo) x lin(E8)) (texpaint.relight).
//                STRATA (gs-family-rock): on the rock field's texels (C7 meta texels: its rock
//                classes, its cliff classes past cliff_deg +- STRATA_RAMP_DEG; the bilinear of the
//                1 m indicator) the normal n tilts toward the measured bedding: per 32 m cell
//                (bilinear weights of the 2 x 2 cells around the texel, a no-strata cell adds
//                nothing) the bedding normal b (strike / dip, game frame) and spacing L give the
//                phase s = b . (x, h, z) / L (world-coherent across cells), its plan wavelength L /
//                |grad_plan(b . p)| -> octaveKeep; n' = normalize(n + k w sum(wc keep sin(2 pi s)
//                (b - (b . n) n))), k = ground.strata; lam / n_y from n'. Light only: no albedo,
//                no geometry; a texel off the field is bit-identical.
import { haloPx, gnoise, waysPassRaster } from './groundways.js';
import { genomeTexParams, genomeTexel } from './genometex.js';
import { groundAt } from '../../groundm/lookup.js';

const f32 = Math.fround;
// SEALED-MAT (road-keeps-colour): sealed class key -> the wayslook kind whose flat look colour it
// paints (surfacegen SEALED_MAT); the levels past WAYS_MAX_J read the ways' 4 m mass (WAYS-Q4), not the 1 m field
export const SEALED_MAT = { asphalt: 'asphalt', paved: 'paving', ballast: 'ballast' };
export const WAYS_MAX_J = 3;
// SEALED-MAT's palette entries: {key: {base: M, patch: M, mid: 0}}, M = the kind's (base + patch) / 2
// (groundways prepWays M; a {palette} kind reads pal.palette[name]) — a flat colour, no mid term
export function sealedPalette(pal, look) {
  const o = {};
  for (const [key, kind] of Object.entries(SEALED_MAT)) {
    const c = look && look.colour && look.colour[kind];
    const pe = c && ('palette' in c ? pal.palette[c.palette] : 'base' in c ? c : null);
    if (!pe) throw new Error(`SEALED-MAT: wayslook colour.${kind} has no base / patch`);
    const M = [0, 1, 2].map((ch) => f32(f32(f32(pe.base[ch]) + f32(pe.patch[ch])) / 2));
    o[key] = { base: M, patch: M, mid: 0 };
  }
  return o;
}
const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
function roundEven(v) {                       // Python round (half to even)
  const r = Math.round(v);
  return Math.abs(v % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r;
}
// 1 at >= 4 texels per wavelength, 0 at <= 2 (python floats)
function octaveKeep(wl, texel) {
  const t = clamp((wl / texel - 2.0) / 2.0, 0.0, 1.0);
  return t * t * (3 - 2 * t);
}
// surfacegen _smooth on a float32 value
function smooth32(t, lo, hi) {
  t = clamp(f32(f32(t - f32(lo)) / f32(hi - lo)), 0, 1);
  return f32(f32(t * t) * f32(3 - f32(2 * t)));
}
export const SLOT_PX = 512;

// ---------------------------------------------------------------- the light model (bake.py; the gate pins every value)
export const LIGHT = {
  CASTER_MIN_M: 0.25, OBJ_MAX_M: 60.0, EPS_M: 0.5, REACH_MAX_M: 160.0, SKY_FINE_M: [2.0, 6.0, 16.0],
  CANOPY_M: [2.0, 5.0], CANOPY_TOP: 0.5, OPEN_K: 0.765, SILHOUETTE: 0.85, TREELINE_M: 2250.0,
  CROWN: { conifer: ['cone', 0.15, 0.85], larch: ['cone', 0.15, 0.70], columnar: ['cone', 0.15, 0.85],
    birch: ['dome', 0.35, 0.70], broadleaf: ['dome', 0.35, 0.85], spreading: ['dome', 0.35, 0.85],
    umbrella: ['dome', 0.50, 0.85], palm: ['dome', 0.60, 0.60] },
  FORM_CFG: { conifer: [0.55, 1.0], larch: [0.45, 0.9], broadleaf: [0.62, 1.0], palm: [0.35, 1.0],
    columnar: [0.28, 1.0], spreading: [0.85, 0.75], umbrella: [0.72, 1.0], birch: [0.50, 0.95] },
  SHRUB: ['dome', 0.0, 0.85], SHRUB_R: 0.8 * 0.85,
};
// the opacity tiers: cls 0 nothing, 1 opaque, 2.. the crown tiers high to low (DrawnCasters.create)
const TIERS = [...new Set([...Object.values(LIGHT.CROWN).map((c) => c[2]), LIGHT.SHRUB[2]])].sort((a, b) => b - a);
export const OPACITY = [0.0, 1.0, ...TIERS];
const CLS_OF = new Map(TIERS.map((o, i) => [o, 2 + i]));
export const RES_PER_STOP = 32;

// ---------------------------------------------------------------- colour (genome.py / surfacegen / texpaint)
export const WHITE = [0.95047, 1.0, 1.08883];
const M_XYZ = [[0.4124564, 0.3575761, 0.1804375], [0.2126729, 0.7151522, 0.0721750], [0.0193339, 0.1191920, 0.9503041]];
export const M_INV = (() => {
  const m = M_XYZ, a = m[0][0], b = m[0][1], c = m[0][2], d = m[1][0], e = m[1][1], f = m[1][2], g = m[2][0], h = m[2][1], i = m[2][2];
  const A = e * i - f * h, B = -(d * i - f * g), C = d * h - e * g, det = a * A + b * B + c * C;
  return [[A / det, -(b * i - c * h) / det, (b * f - c * e) / det],
    [B / det, (a * i - c * g) / det, -(a * f - c * d) / det],
    [C / det, -(a * h - b * g) / det, (a * e - b * d) / det]];
})();
const LD = 6 / 29;
export function lab(r, g, b) {
  const f = [0, 0, 0];
  for (let k = 0; k < 3; k++) {
    const v = (M_XYZ[k][0] * r + M_XYZ[k][1] * g + M_XYZ[k][2] * b) / WHITE[k];
    f[k] = v > LD ** 3 ? Math.cbrt(Math.max(v, 0)) : v / (3 * LD * LD) + 4 / 29;
  }
  return [116 * f[1] - 16, 500 * (f[0] - f[1]), 200 * (f[1] - f[2])];
}
export function labLin(L, A, B) {
  const fy = (L + 16) / 116, f = [fy + A / 500, fy, fy - B / 200], xyz = [0, 0, 0];
  for (let k = 0; k < 3; k++) xyz[k] = (f[k] > LD ? f[k] ** 3 : 3 * LD * LD * (f[k] - 4 / 29)) * WHITE[k];
  return [0, 1, 2].map((k) => M_INV[k][0] * xyz[0] + M_INV[k][1] * xyz[1] + M_INV[k][2] * xyz[2]);
}
const srgbDec = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
function srgbEnc(c) {                         // surfacegen _srgb_enc (float64, clipped)
  c = clamp(c, 0.0, 1.0);
  return c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
}
// texpaint._srgb_to_lin / _lin_to_srgb8 (float32)
export function srgbToLin32(x) {
  x = f32(x);
  return x <= f32(0.04045) ? f32(x / f32(12.92)) : f32(f32(f32(x + f32(0.055)) / f32(1.055)) ** f32(2.4));
}
export function linToSrgb8(x) {
  x = clamp(f32(x), 0, 1);
  const s = x <= f32(0.0031308) ? f32(x * f32(12.92)) : f32(f32(f32(1.055) * f32(x ** f32(1 / 2.4))) - f32(0.055));
  return Math.floor(f32(f32(s * 255) + 0.5));
}
export const LUT8 = Float32Array.from({ length: 256 }, (_, i) => srgbToLin32(f32(i / 255)));
// genome.class_mean: the record's mean_lin with its elev / slope Lab terms
function classMean(rec, L0, h, sdeg) {
  const e = rec.elev, s = rec.slope, de = (h - e.ref_m) / 100.0, ds = (sdeg - s.ref_deg) / 10.0;
  const c = labLin(L0[0] + de * e.dlab_per_100m[0] + ds * s.dlab_per_10deg[0],
    L0[1] + de * e.dlab_per_100m[1] + ds * s.dlab_per_10deg[1], L0[2] + de * e.dlab_per_100m[2] + ds * s.dlab_per_10deg[2]);
  return [Math.max(c[0], 1e-6), Math.max(c[1], 1e-6), Math.max(c[2], 1e-6)];
}
// surfacegen measured_lin: linear reflectance -> the painter's scale (anchor, grass_sat)
// (exported: mapclass.js tints the Map's measured grass the same way)
export function measuredLin(r, g, b, anchor, sat) {
  r = Math.max(r, 1e-5) * anchor; g = Math.max(g, 1e-5) * anchor; b = Math.max(b, 1e-5) * anchor;
  const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const c = [y * (r / y) ** sat, y * (g / y) ** sat, y * (b / y) ** sat];
  const k = y / Math.max(0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2], 1e-9);
  return [c[0] * k, c[1] * k, c[2] * k];
}

// ---------------------------------------------------------------- grids (surfacegen _bspline / _bilin, bake SHD2)
// the cubic B-spline of one channel of a (h, w, nc) u8 / f32 grid at cell (k + 0.5) cell,
// clamped taps: float32 weights, the numpy's summation order (x taps, then z taps)
function bsplineW(u) {
  const f = Math.floor(u), t = u - f, t2 = t * t, t3 = t2 * t;
  return [f, f32((1 - t) ** 3 / 6.0), f32((3 * t3 - 6 * t2 + 4) / 6.0), f32((-3 * t3 + 3 * t2 + 3 * t + 1) / 6.0), f32(t3 / 6.0)];
}
function bspline(G, w, h, nc, ch, cell, x, z) {
  const wx = bsplineW(x / cell - 0.5), wz = bsplineW(z / cell - 0.5);
  let acc = 0;
  for (let b = 0; b < 4; b++) {
    const r = clamp(wz[0] + b - 1, 0, h - 1);
    let t = 0;
    for (let a = 0; a < 4; a++) {
      const c = clamp(wx[0] + a - 1, 0, w - 1);
      const p = f32(wx[1 + a] * G[(r * w + c) * nc + ch]);
      t = a === 0 ? p : f32(t + p);
    }
    const q = f32(wz[1 + b] * t);
    acc = b === 0 ? q : f32(acc + q);
  }
  return acc;
}
// bilinear of a grid at (k + 0.5) cell, clamped (surfacegen _bilin's float32 lerps); win: G holds only
// the cells [x0, x0 + w) x [z0, z0 + h) of the w x h grid (the reads clamp to the grid first)
function bilin(G, w, h, cell, x, z, scale = 1, win = null) {
  const u = x / cell - 0.5, v = z / cell - 0.5, fu = Math.floor(u), fv = Math.floor(v);
  const i0 = clamp(fu, 0, w - 1), i1 = clamp(fu + 1, 0, w - 1), j0 = clamp(fv, 0, h - 1), j1 = clamp(fv + 1, 0, h - 1);
  const tx = f32(clamp(u - fu, 0, 1)), tz = f32(clamp(v - fv, 0, 1)), ux = f32(1 - tx), uz = f32(1 - tz);
  const ox = win ? win.x0 : 0, oz = win ? win.z0 : 0, ow = win ? win.w : w;
  const r0 = (j0 - oz) * ow - ox, r1 = (j1 - oz) * ow - ox;
  const a = G[r0 + i0] * scale, b = G[r0 + i1] * scale, c = G[r1 + i0] * scale, d = G[r1 + i1] * scale;
  return f32(f32(f32(f32(a * ux) + f32(b * tx)) * uz) + f32(f32(f32(c * ux) + f32(d * tx)) * tz));
}

// ---------------------------------------------------------------- noise fields (surfacegen _fbm / _patch_field / _lowfreq)
function fbm(X, Z, wls, seed, texel) {
  let out = 0, wsum = 0, amp = 1.0;
  for (let i = 0; i < wls.length; i++) {
    const k = octaveKeep(wls[i], texel);
    if (k > 0) out = f32(out + f32(f32(amp) * f32(0.5 + f32(f32(k) * f32(gnoise(X, Z, wls[i], seed + i * 101) - 0.5)))));
    else out = f32(out + f32(amp * 0.5));
    wsum += amp;
    amp *= 0.55;
  }
  return f32(out / f32(wsum));
}
// the 47 / 21 m patch field, domain-warped; x32: X / Z are float32 rasters (the warp sums in float32)
function patchField(X, Z, texel, x32) {
  const wx = f32(25.0 * f32(f32(2.0 * gnoise(X, Z, 120.0, 5)) - 1.0)), wz = f32(25.0 * f32(f32(2.0 * gnoise(X, Z, 120.0, 6)) - 1.0));
  return x32 ? fbm(f32(X + wx), f32(Z + wz), [47.0, 21.0], 11, texel) : fbm(X + wx, Z + wz, [47.0, 21.0], 11, texel);
}
// 'patch' at one texel: direct where texel 1.5 >= 1 m, else the bilinear of the world 1 m lattice
function patchAt(x, z, texel, lat) {
  if (texel * 1.5 >= 1.0) return patchField(x, z, texel, true);
  const i0 = Math.floor(x), j0 = Math.floor(z), tx = f32(x - i0), tz = f32(z - j0), ux = f32(1 - tx), uz = f32(1 - tz);
  const g = (i, j) => {
    const k = i + ',' + j;
    let v = lat.get(k);
    if (v === undefined) { v = patchField(i, j, texel, false); lat.set(k, v); }
    return v;
  };
  const p = f32(f32(g(i0, j0) * ux) + f32(g(i0 + 1, j0) * tx)), q = f32(f32(g(i0, j0 + 1) * ux) + f32(g(i0 + 1, j0 + 1) * tx));
  return f32(f32(p * uz) + f32(q * tz));
}

// ---------------------------------------------------------------- the world
export function prepWorld(inp) {
  const W = { ...inp, keyById: new Map(), meas: new Set(inp.pal.measured_keys) };
  // the class map's window (cm[(cz - oz) ow + cx - ox]); the whole map by default
  const cw = inp.cmWin || { x0: 0, z0: 0, w: inp.w, h: inp.h };
  if (inp.cm.length !== cw.w * cw.h || (inp.sheet && inp.sheet.length !== cw.w * cw.h)) throw new Error(`groundref: cm / sheet ${inp.cm.length} cells, the window ${cw.w} x ${cw.h}`);
  W.ox = cw.x0; W.oz = cw.z0; W.ow = cw.w;
  for (const [k, id] of Object.entries(inp.ids)) if (!W.keyById.has(id)) W.keyById.set(id, k);
  // SEALED-MAT: the sealed classes paint their way kind's flat colour (the palette path), never a record
  const sp = sealedPalette(inp.pal, inp.look || (inp.ways && inp.ways.look));
  W.pal = { ...inp.pal, palette: { ...inp.pal.palette, ...sp } };
  W.sealed = new Uint8Array(256);
  for (const k of Object.keys(sp)) if (inp.ids[k] !== undefined) W.sealed[inp.ids[k]] = 1;
  // genome mode: the record a class paints (forest -> forest_floor when the doc has it)
  const recs = inp.genome.classes;
  W.gRec = new Map(Object.keys(recs).map((k) => [k, k]));
  if (recs.forest_floor) W.gRec.set('forest', 'forest_floor');
  for (const k of Object.keys(sp)) W.gRec.delete(k);
  W.gP = new Map();
  W.gLab = new Map();
  for (const k of Object.keys(recs)) {
    W.gP.set(k, genomeTexParams(recs[k], k, inp.gtTable));
    const m = recs[k].mean_lin;
    W.gLab.set(k, lab(m[0], m[1], m[2]));
  }
  if (inp.s2col) {
    const { img, w, h } = inp.s2col, lin = new Float32Array(w * h * 3), cov = new Float32Array(w * h);
    for (let q = 0; q < w * h; q++) {
      const on = img[3 * q] || img[3 * q + 1] || img[3 * q + 2] ? 1 : 0;
      cov[q] = on;
      for (let c = 0; c < 3; c++) lin[3 * q + c] = f32(srgbDec(img[3 * q + c] / 255.0) * on);
    }
    W.s2 = { lin, cov, w, h, cell: inp.s2col.cell, x0: inp.s2col.x0, z0: inp.s2col.z0 };
  }
  W.crownDiscs = new Map();                   // per level j (y = that level's drawn ground)
  const L = inp.light, az = L.sunAzDeg * Math.PI / 180, alt = L.sunAltDeg * Math.PI / 180;
  W.sun = { sx: Math.sin(az) * Math.cos(alt), sy: Math.cos(az) * Math.cos(alt), sz: Math.sin(alt),
    tan: Math.tan(alt), dx: Math.sin(az), dy: -Math.cos(az) };
  W.shd2 = { vis: [...inp.shd2.vis].sort((a, b) => a.cell - b.cell), sky: [...inp.shd2.sky].sort((a, b) => a.cell - b.cell) };
  W.strata = inp.rockfield ? strataField(inp.rockfield.meta, inp.rockfield.img, inp.ids) : null;
  W.strataK = clamp(+inp.strata || 0, 0, 1);
  W.tile = inp.tiling && inp.exemplars ? tileWorld(recs, inp.exemplars) : null;
  return W;
}
// one slot's windowed terms on a world prepWorld made once (its constants shared, read only): s = {cm,
// cmWin, sheet, ways, ground, shd2, trees, shrubs} as prepWorld's inputs (slotworld.js, the pre-paint)
export function slotPart(W0, s) {
  const cw = s.cmWin;
  if (s.cm.length !== cw.w * cw.h || (s.sheet && s.sheet.length !== cw.w * cw.h)) throw new Error(`groundref: cm / sheet ${s.cm.length} cells, the window ${cw.w} x ${cw.h}`);
  return { ...W0, cm: s.cm, cmWin: cw, ox: cw.x0, oz: cw.z0, ow: cw.w, sheet: s.sheet, ways: s.ways, ground: s.ground,
    crowns: { ...W0.crowns, trees: s.trees, shrubs: s.shrubs }, crownDiscs: new Map(),
    shd2: { vis: [...s.shd2.vis].sort((a, b) => a.cell - b.cell), sky: [...s.shd2.sky].sort((a, b) => a.cell - b.cell) } };
}

// ---------------------------------------------------------------- 0. heights: the TIN rasteriser
const nodeSide = (k) => 128 * (1 << k);
// the level-j nodes over world [xa, xb] x [za, zb] in a fixed order (ty, tx)
function nodesOver(world, j, xa, xb, za, zb) {
  // 1 m margins: a node whose border a sample lies on is always in the list (first wins = the
  // same triangle in every window)
  const S = nodeSide(j), off = 0.25 * (1 << j), out = [];
  for (let ty = Math.floor((za - 1 - off) / S); ty <= Math.floor((zb + 1 - off) / S); ty++) {
    for (let tx = Math.floor((xa - 1 - off) / S); tx <= Math.floor((xb + 1 - off) / S); tx++) {
      const n = world.ground.get(`${j}_${tx}_${ty}`);
      if (n) out.push(n);
    }
  }
  return out;
}
// The RTIN lattice is 2 samples (level j: 0.5 2^j m, the sample 0.25 2^j m; the crown grid's
// cell divides it), so EVERY sample centre lies on a 45-degree diagonal x +- z = k lattice: on a
// split cell's hypotenuse the owner would be a tie-break (here the index order, on the GPU its
// fill rule; the two planes' gradients differ -> slope / class / E flip, a DETERMINISTIC
// checkerboard line of dE 20..57 along every crease). The triangle is picked at the centre +
// (TIN_EX, TIN_EZ) samples — >= 1/8 sample (perpendicular) off every lattice line and diagonal
// for a lattice of 1, 2, 4 or 8 samples, centred or cornered: twice the rasteriser's 1/16-px
// snap (GL's minimum SUBPIXEL_BITS 4) — and its plane is evaluated at the centre. groundgen's TIN
// pass shifts its map by the same amount.
export const TIN_EX = 0.3125, TIN_EZ = 0.125;
// heights + the flat triangle gradient (dh/dx east, dh/dz south) at the sample centres xs x zs
// (uniform, increasing; float32 coordinates) from the level-j nodes. -> {h, gx, gz} Float32Array
export function tinRaster(world, j, xs, zs) {
  const nx = xs.length, nz = zs.length, h = new Float32Array(nx * nz), gx = new Float32Array(nx * nz), gz = new Float32Array(nx * nz);
  const cov = new Uint8Array(nx * nz), dx = nx > 1 ? xs[1] - xs[0] : 1, dz = nz > 1 ? zs[1] - zs[0] : 1;
  const S = nodeSide(j), ex = TIN_EX * dx, ez = TIN_EZ * dz;
  for (const n of nodesOver(world, j, xs[0], xs[nx - 1], zs[0], zs[nz - 1])) {
    const ox = n.tx * S, oz = n.ty * S, { x, z, y, idx, lab } = n;
    for (let t = 0; t < idx.length / 3; t++) {
      if (lab && lab[t] & 16) continue;                      // NOSTAND: walls, seal
      const a = idx[3 * t], b = idx[3 * t + 1], c = idx[3 * t + 2];
      const ax = ox + x[a], az = oz + z[a], ux = x[b] - x[a], uz = z[b] - z[a], vx = x[c] - x[a], vz = z[c] - z[a];
      const den = ux * vz - uz * vx;
      if (!(Math.abs(den) > 1e-12)) continue;
      const uy = y[b] - y[a], vy = y[c] - y[a];
      const tgx = f32((uy * vz - vy * uz) / den), tgz = f32((ux * vy - vx * uy) / den);
      const i0 = Math.max(0, Math.ceil((Math.min(ax, ax + ux, ax + vx) - ex - xs[0]) / dx - 1e-9));
      const i1 = Math.min(nx - 1, Math.floor((Math.max(ax, ax + ux, ax + vx) - ex - xs[0]) / dx + 1e-9));
      const k0 = Math.max(0, Math.ceil((Math.min(az, az + uz, az + vz) - ez - zs[0]) / dz - 1e-9));
      const k1 = Math.min(nz - 1, Math.floor((Math.max(az, az + uz, az + vz) - ez - zs[0]) / dz + 1e-9));
      for (let k = k0; k <= k1; k++) {
        const pz = zs[k] - az, qz = pz + ez;
        for (let i = i0; i <= i1; i++) {
          const q = k * nx + i;
          if (cov[q]) continue;                                // first node wins on a shared border
          const qx = xs[i] - ax + ex, m1 = (qx * vz - qz * vx) / den, m2 = (ux * qz - uz * qx) / den;
          if (m1 < -1e-9 || m2 < -1e-9 || m1 + m2 > 1 + 1e-9) continue;
          const px = xs[i] - ax, l1 = (px * vz - pz * vx) / den, l2 = (ux * pz - uz * px) / den;
          h[q] = f32(y[a] + l1 * uy + l2 * vy); gx[q] = tgx; gz[q] = tgz; cov[q] = 1;
        }
      }
    }
  }
  // past every node (the world's edge): the nearest covered sample of the row, then the column
  const copy = (d, s) => { h[d] = h[s]; gx[d] = gx[s]; gz[d] = gz[s]; cov[d] = 2; };
  for (let k = 0; k < nz; k++) {
    let a = -1, b = -1;
    for (let i = 0; i < nx; i++) if (cov[k * nx + i] === 1) { if (a < 0) a = i; b = i; }
    if (a < 0) continue;
    for (let i = 0; i < a; i++) copy(k * nx + i, k * nx + a);
    for (let i = b + 1; i < nx; i++) copy(k * nx + i, k * nx + b);
    for (let i = a; i <= b; i++) if (!cov[k * nx + i]) copy(k * nx + i, k * nx + (i - 1));
  }
  const rowOk = (k) => cov[k * nx] > 0;
  let ka = -1, kb = -1;
  for (let k = 0; k < nz; k++) if (rowOk(k)) { if (ka < 0) ka = k; kb = k; }
  if (ka < 0) throw new Error(`groundref: no level-${j} ground node under [${xs[0]}, ${xs[nx - 1]}] x [${zs[0]}, ${zs[nz - 1]}]`);
  for (let k = 0; k < nz; k++) {
    if (rowOk(k)) continue;
    const s = k < ka ? ka : kb;
    for (let i = 0; i < nx; i++) copy(k * nx + i, s * nx + i);
  }
  return { h, gx, gz };
}
// the ORACLE's heights (opts.heights 'dtm'): surfacegen _bilin of the 1 m dtm + np.gradient
function dtmRaster(world, xs, zs) {
  const nx = xs.length, nz = zs.length, h = new Float32Array(nx * nz), gx = new Float32Array(nx * nz), gz = new Float32Array(nx * nz);
  for (let k = 0; k < nz; k++) for (let i = 0; i < nx; i++) h[k * nx + i] = bilin(world.dtm, world.w, world.h, 1.0, xs[i], zs[k]);
  const t = xs[1] - xs[0];
  for (let k = 0; k < nz; k++) {
    for (let i = 0; i < nx; i++) {
      const q = k * nx + i;
      gx[q] = i === 0 ? f32(f32(h[q + 1] - h[q]) / f32(t)) : i === nx - 1 ? f32(f32(h[q] - h[q - 1]) / f32(t))
        : f32(f32(h[q + 1] - h[q - 1]) / f32(2 * t));
      gz[q] = k === 0 ? f32(f32(h[q + nx] - h[q]) / f32(t)) : k === nz - 1 ? f32(f32(h[q] - h[q - nx]) / f32(t))
        : f32(f32(h[q + nx] - h[q - nx]) / f32(2 * t));
    }
  }
  return { h, gx, gz };
}

// ---------------------------------------------------------------- 1. class albedo
// the hand palette (surfacegen _class_albedo, genome mode: no ortho fields)
function paletteAlbedo(world, key, X, Z, F) {
  const p = world.pal.palette[key] || world.pal.palette.meadow, t = F.patch, u = f32(1 - t);
  const d = f32(F.mid - 0.5), col = [0, 0, 0];
  for (let c = 0; c < 3; c++) col[c] = f32(f32(f32(p.base[c] * u) + f32(p.patch[c] * t)) + f32(d * (2 * p.mid)));
  if (key === 'heath' || key === 'wetland') {
    const bl = f32(0.35 * smooth32(F.mid, 0.55, 0.75)), k = f32(1 - bl);
    for (let c = 0; c < 3; c++) col[c] = f32(f32(col[c] * k) + f32(p.patch[c] * bl));
  } else if (key === 'rock') {
    const band = f32(0.5 + f32(0.5 * f32(Math.sin(f32(f32(F.elev * f32(2 * Math.PI / 7.0)) + f32(F.patch * 5.0))))));
    const s = F.slope, n = f32(7.0 / f32(f32(Math.max(s, f32(1e-3))) * F.texel));
    const amp = f32(smooth32(s, 0.7, 1.1) * smooth32(n, 3.0, 6.0)), m = f32(0.98 + f32(amp * f32(f32(0.16 * band) - 0.08)));
    for (let c = 0; c < 3; c++) col[c] = f32(col[c] * m);
  } else if (key === 'sport') {
    const amp = octaveKeep(8.0, F.texel), st = f32(0.5 + f32(0.5 * f32(Math.sin(f32(X * f32(2 * Math.PI / 8.0))))));
    const m = f32(0.97 + f32(0.06 * f32(0.5 + f32(f32(amp) * f32(st - 0.5)))));
    for (let c = 0; c < 3; c++) col[c] = f32(col[c] * m);
  }
  return col;
}
// ---- exemplar tiling (gs-family-wet-gravel): Heitz-Neyret histogram-preserving blend of the
// genome's measured exemplar (C4, a de-lit 128² window). T = the exemplar's relative albedo
// (lin / its mean per channel) where the genome T would be
export const TILE_KEYS = ['wetland', 'gravel', 'dirt'];   // the family (water beds paint gravel)
export const TILE = { CELL: 0.25, LUT_N: 257, LUT_X: 4.0 };   // lattice edge / extent; LUT knots over +-LUT_X
function normInv(p) {                         // Acklam's inverse normal CDF (float64)
  const a = [-39.69683028665376, 220.9460984245205, -275.9285104469687, 138.3577518672690, -30.66479806614716, 2.506628277459239];
  const b = [-54.47609879822406, 161.5858368580409, -155.6989798598866, 66.80131188771972, -13.28068155288572];
  const c = [-0.007784894002430293, -0.3223964580411365, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783];
  const d = [0.007784695709041462, 0.3224671290700398, 2.445134137142996, 3.754408661907416];
  const tail = (q) => (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  if (p < 0.02425) return tail(Math.sqrt(-2 * Math.log(p)));
  if (p > 1 - 0.02425) return -tail(Math.sqrt(-2 * Math.log(1 - p)));
  const q = p - 0.5, r = q * q;
  return (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q / (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
}
function interp(x, xp, fp) {                  // numpy interp (xp ascending, ties: the last)
  const n = xp.length;
  if (x < xp[0]) return fp[0];
  if (x >= xp[n - 1]) return fp[n - 1];
  let lo = 0, hi = n - 1;                     // the last j with xp[j] <= x
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (xp[m] <= x) lo = m; else hi = m; }
  return fp[lo] + (x - xp[lo]) * (fp[lo + 1] - fp[lo]) / (xp[lo + 1] - xp[lo]);
}
export function tileSeed(key) {               // FNV-1a 32 of the key
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 0x01000193);
  return h >>> 0;
}
export function tileHash(i, j, s) {           // uint32 of a lattice vertex (int32 i j, uint32 s)
  let h = Math.imul(i, 0x27d4eb2d) ^ Math.imul(j, 0x165667b1) ^ Math.imul(s, 0x9e3779b9);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
}
// exemplar (Uint8Array n n 3 sRGB, row 0 north, n a power of 2, pxM m a texel) -> {n, pxM, extent,
// levels, G: [Float32Array (n >> l)² 3] the Gaussianised box mips, lut Float32Array levels 3 LUT_N}
// THE prep is tilePrepSteps (R8 M5b): a step per channel pass, then one per mip level
// (groundgen _worldTiles: a step a scan part); tilePrep = its drain
export function tilePrep(img, n, pxM, key) {
  const it = tilePrepSteps(img, n, pxM, key);
  let s;
  do s = it.next(); while (!s.done);
  return s.value;
}
export function* tilePrepSteps(img, n, pxM, key) {
  const N = n * n, r = new Float64Array(N * 3), g = new Float64Array(N * 3);
  for (let c = 0; c < 3; c++) {
    if (c) yield;
    let s = 0;
    for (let q = 0; q < N; q++) { r[3 * q + c] = srgbDec(img[3 * q + c] / 255.0); s += r[3 * q + c]; }
    const m = Math.max(s / N, 1e-6);
    for (let q = 0; q < N; q++) r[3 * q + c] /= m;
    // the rank by (value, index): a counting sort of the bytes (r is monotone in the byte)
    const at = new Uint32Array(257);
    for (let q = 0; q < N; q++) at[img[3 * q + c] + 1]++;
    for (let v = 0; v < 256; v++) at[v + 1] += at[v];
    for (let q = 0; q < N; q++) g[3 * q + c] = normInv((at[img[3 * q + c]]++ + 0.5) / N);
  }
  const levels = Math.round(Math.log2(n)) + 1, G = [], lut = new Float32Array(levels * 3 * TILE.LUT_N);
  let gl = g, rl = r;
  for (let l = 0; l < levels; l++) {
    yield;
    const nl = n >> l, Nl = nl * nl;
    G.push(Float32Array.from(gl));
    const pk = Float64Array.from({ length: Nl }, (_, k) => (k + 0.5) / Nl);
    for (let c = 0; c < 3; c++) {
      const gs = Float64Array.from({ length: Nl }, (_, q) => gl[3 * q + c]).sort();
      const rs = Float64Array.from({ length: Nl }, (_, q) => rl[3 * q + c]).sort();
      for (let i = 0; i < TILE.LUT_N; i++) {
        const x = -TILE.LUT_X + 2 * TILE.LUT_X * i / (TILE.LUT_N - 1);
        lut[(l * 3 + c) * TILE.LUT_N + i] = Nl === 1 ? rs[0] : interp(interp(x, gs, pk), pk, rs);
      }
    }
    if (nl === 1) break;
    const h = nl >> 1, g2 = new Float64Array(h * h * 3), r2 = new Float64Array(h * h * 3);
    for (let k = 0; k < h; k++) for (let i = 0; i < h; i++) for (let c = 0; c < 3; c++) {
      const at = (a, kk, ii) => a[3 * (kk * nl + ii) + c], q = 3 * (k * h + i) + c;
      g2[q] = (at(gl, 2 * k, 2 * i) + at(gl, 2 * k, 2 * i + 1) + at(gl, 2 * k + 1, 2 * i) + at(gl, 2 * k + 1, 2 * i + 1)) / 4;
      r2[q] = (at(rl, 2 * k, 2 * i) + at(rl, 2 * k, 2 * i + 1) + at(rl, 2 * k + 1, 2 * i) + at(rl, 2 * k + 1, 2 * i + 1)) / 4;
    }
    gl = g2; rl = r2;
  }
  return { n, pxM, extent: n * pxM, levels, G, lut, seed: tileSeed(key) };
}
// the tiled records: TILE_KEYS whose genome record carries an exemplar the inputs hold
// (exemplars {key: {img, n, pxM}}) -> Map key -> tilePrep | null
export function tileWorld(recs, exemplars) {
  const m = new Map();
  for (const k of TILE_KEYS) {
    const e = recs[k] && recs[k].exemplar && exemplars[k];
    if (e) m.set(k, tilePrep(e.img, e.n, e.pxM, k));
  }
  return m.size ? m : null;
}
export const tileLevel = (E, texel) => clamp(Math.round(Math.log2(texel / E.pxM)), 0, E.levels - 1);
// the lattice (equilateral, edge S = CELL x extent): skew (X - Z / sqrt 3, 2 Z / sqrt 3) / S; its
// triangle's 3 vertices [i, j, w]; vertex (i, j) at (S (i + j / 2), S j sqrt 3 / 2)
export function tileTri(sx, sy) {
  const bi = Math.floor(sx), bj = Math.floor(sy), fx = sx - bi, fy = sy - bj, fz = 1 - fx - fy;
  return fz > 0 ? [[bi, bj, fz], [bi, bj + 1, fy], [bi + 1, bj, fx]]
    : [[bi + 1, bj + 1, -fz], [bi + 1, bj, 1 - fy], [bi, bj + 1, 1 - fx]];
}
// T at world (X, Z): per vertex a hashed window centre inside the exemplar (margin S + a level texel:
// no wrap, no seam), the level's bilinear G there; G = sum w g / sqrt(sum w²) -> the level's LUT
export function tileTexel(E, X, Z, texel) {
  const l = tileLevel(E, texel), nl = E.n >> l, pl = E.pxM * (1 << l), S = TILE.CELL * E.extent, R3 = Math.sqrt(3);
  const margin = S + pl, span = Math.max(0, E.extent - 2 * margin), Gl = E.G[l];
  const V = tileTri((X - Z / R3) / S, 2 * Z / (R3 * S)), acc = [0, 0, 0];
  let w2 = 0;
  for (const [i, j, w] of V) {
    if (!(w > 0)) continue;
    const h1 = tileHash(i, j, E.seed), h2 = tileHash(i, j, (E.seed + 1) >>> 0);
    const qx = margin + (h1 >>> 8) / 16777216 * span + (X - S * (i + j / 2)), qz = margin + (h2 >>> 8) / 16777216 * span + (Z - S * j * R3 / 2);
    const u = qx / pl - 0.5, v = qz / pl - 0.5, i0 = Math.floor(u), k0 = Math.floor(v), tu = u - i0, tv = v - k0;
    const ia = clamp(i0, 0, nl - 1), ib = clamp(i0 + 1, 0, nl - 1), ka = clamp(k0, 0, nl - 1), kb = clamp(k0 + 1, 0, nl - 1);
    for (let c = 0; c < 3; c++) {
      const a = Gl[3 * (ka * nl + ia) + c] * (1 - tu) + Gl[3 * (ka * nl + ib) + c] * tu;
      const b = Gl[3 * (kb * nl + ia) + c] * (1 - tu) + Gl[3 * (kb * nl + ib) + c] * tu;
      acc[c] += w * (a * (1 - tv) + b * tv);
    }
    w2 += w * w;
  }
  const iw = 1 / Math.sqrt(w2), T = [0, 0, 0];
  for (let c = 0; c < 3; c++) {
    const u = clamp((acc[c] * iw + TILE.LUT_X) / (2 * TILE.LUT_X) * (TILE.LUT_N - 1), 0, TILE.LUT_N - 1);
    const i0 = Math.min(Math.floor(u), TILE.LUT_N - 2), t = u - i0, o = (l * 3 + c) * TILE.LUT_N;
    T[c] = f32(E.lut[o + i0] * (1 - t) + E.lut[o + i0 + 1] * t);
  }
  return T;
}

// genome mode (surfacegen _genome_albedo): sRGB 0..255 float32
function genomeAlbedo(world, key, X, Z, F, opts) {
  const rk = world.gRec.get(key), rec = world.genome.classes[rk];
  const ex = !opts.noTiling && world.tile ? world.tile.get(rk) : null;
  const T = ex ? tileTexel(ex, X, Z, F.texel) : genomeTexel(world.gP.get(rk), X, Z, F.texel);
  const sdeg = f32(f32(Math.atan(F.slope)) * f32(180 / Math.PI));
  const base = classMean(rec, world.gLab.get(rk), F.elev, sdeg);
  if (!opts.noResidual) {
    const R = world.residual, rx = f32(X - R.x0), rz = f32(Z - R.z0);
    for (let c = 0; c < 3; c++) base[c] *= 2 ** ((bspline(R.codes, R.w, R.h, 3, c, R.cell, rx, rz) - 128.0) / RES_PER_STOP);
  }
  for (let c = 0; c < 3; c++) base[c] *= world.anchor;
  if (world.s2 && world.meas.has(key) && !opts.noS2) {
    const s = world.s2, sx = f32(X - s.x0), sz = f32(Z - s.z0), cw = bspline(s.cov, s.w, s.h, 1, 0, s.cell, sx, sz);
    if (cw > 1e-6) {
      const m = measuredLin(bspline(s.lin, s.w, s.h, 3, 0, s.cell, sx, sz) / cw, bspline(s.lin, s.w, s.h, 3, 1, s.cell, sx, sz) / cw,
        bspline(s.lin, s.w, s.h, 3, 2, s.cell, sx, sz) / cw, world.anchor, world.grassSat);
      for (let c = 0; c < 3; c++) base[c] = cw * m[c] + (1.0 - cw) * base[c];
    }
  }
  return [f32(srgbEnc(T[0] * base[0]) * 255.0), f32(srgbEnc(T[1] * base[1]) * 255.0), f32(srgbEnc(T[2] * base[2]) * 255.0)];
}
function classAlbedo(world, cid, X, Z, F, opts) {
  if (cid === world.waterId) {
    // WATER-G: one colour to the shore; a sheet lake's bed = the gravel's paint
    const cx = clamp(Math.trunc(X), 0, world.w - 1), cz = clamp(Math.trunc(Z), 0, world.h - 1);
    if (world.sheet && world.sheet[(cz - world.oz) * world.ow + cx - world.ox]) return classAlbedo(world, world.ids.gravel, X, Z, F, opts);
    return world.pal.palette.water.deep.map((v) => f32(v));
  }
  const key = world.keyById.get(cid) || 'meadow';
  return world.gRec.has(key) ? genomeAlbedo(world, key, X, Z, F, opts) : paletteAlbedo(world, key, X, Z, F);
}
// surfacegen _zone_albedo (the apron past the class map)
function zoneAlbedo(world, F) {
  const b = world.bands, P = world.pal.palette, ez = f32(F.elev + f32(f32(F.patch - 0.5) * f32(b.jitter_m)));
  const mot = f32(0.9 + f32(0.2 * F.mid)), col = [0, 0, 0];
  let ws = 0;
  for (const [w, key] of [[f32(1 - smooth32(ez, ...b.meadow_m)), 'meadow'],
    [f32(smooth32(ez, ...b.turf_m) * f32(1 - smooth32(ez, ...b.turf_out_m))), 'turf'],
    [smooth32(ez, ...b.scree_m), 'scree'], [smooth32(ez, ...b.rock_m), 'rock'], [f32(smooth32(ez, ...b.snow_m) * 2.0), 'snow']]) {
    for (let c = 0; c < 3; c++) col[c] = f32(col[c] + f32(w * f32(P[key].base[c] * mot)));
    ws = f32(ws + w);
  }
  const d = f32(Math.max(ws, f32(1e-6)));
  return [f32(col[0] / d), f32(col[1] / d), f32(col[2] / d)];
}

// ---------------------------------------------------------------- 4. crowns
function speciesLut(hist, sp) {               // bake._species_lut (vegpaint.js speciesLut)
  let tot = 0;
  for (let b = 0; b < 255; b++) tot += hist[b] || 0;
  if (!sp.length || !tot) return null;
  const lum = (hx) => { const c = parseInt(hx.slice(1), 16); return ((c >> 16) & 255) * 0.35 + ((c >> 8) & 255) * 0.5 + (c & 255) * 0.15; };
  const ls = sp.map((s) => lum(s.color)), order = sp.map((_, i) => i).sort((a, b) => ls[a] - ls[b] || a - b);
  const cum = [];
  let c = 0;
  for (const oi of order) { c += sp[oi].share; cum.push(c); }
  const lut = new Uint8Array(255);
  let acc = 0, k = 0;
  for (let b = 0; b < 255; b++) {
    acc += hist[b] || 0;
    while (k < order.length - 1 && acc > cum[k] * tot) k++;
    lut[b] = order[k];
  }
  return lut;
}
function resolveSpecies(list) {               // bake._resolve_species (vegpaint.js resolveSpecies)
  if (!list || !list.length) throw new Error('groundref: vegetation.species missing: the forest has no species to draw');
  const sp = list.slice(0, 4).map((s) => ({ form: s.form in LIGHT.FORM_CFG ? s.form : 'conifer', color: s.color,
    share: typeof s.share === 'number' && Number.isFinite(s.share) && s.share > 0 ? s.share : 0 }));
  const tot = sp.reduce((a, s) => a + s.share, 0);
  for (const s of sp) s.share = tot <= 0 ? 1 / sp.length : s.share / tot;
  return sp;
}
// the drawn crowns + shrubs of the crown pack (bake.DrawnCasters _tree_discs / _shrub_discs),
// y = the level-j drawn ground at the crown -> {x, z, y, top, base, r, dome, cls} arrays
export function crownDiscs(world, j) {
  if (world.crownDiscs.has(j)) return world.crownDiscs.get(j);
  const yAt = (x, z) => {
    const S = nodeSide(j), off = 0.25 * (1 << j), tx = Math.floor((x - off) / S), ty = Math.floor((z - off) / S);
    for (const [a, b] of [[tx, ty], [tx - 1, ty], [tx, ty - 1], [tx - 1, ty - 1]]) {
      const n = world.ground.get(`${j}_${a}_${b}`);
      const v = n ? groundAt(n, x - a * S, z - b * S) : NaN;
      if (v === v) return v;
    }
    return NaN;
  };
  const D = crownDiscsOf(world.crowns, world.crowns.trees, world.crowns.shrubs, yAt);
  world.crownDiscs.set(j, D);
  return D;
}
// the discs of some crown / shrub pack tiles (groundgen.js: a slot's window), y = yAt(x, z)
// (NaN: no drawn ground, the crown is skipped); C = {species, appearHist, treelineM}
export function crownDiscsOf(C, trees, shrubs, yAt) {
  const sp = resolveSpecies(C.species), lut = speciesLut(C.appearHist, sp);
  let major = 0;
  for (let i = 1; i < sp.length; i++) if (sp[i].share > sp[major].share) major = i;
  const tl = C.treelineM || LIGHT.TREELINE_M, D = { x: [], z: [], y: [], top: [], base: [], r: [], dome: [], cls: [] };
  const push = (x, z, y, top, base, r, dome, cls) => {
    D.x.push(x); D.z.push(z); D.y.push(y); D.top.push(top); D.base.push(base); D.r.push(r); D.dome.push(dome); D.cls.push(cls);
  };
  for (const t of trees) {
    for (let k = 0; k < t.n; k++) {
      const x = t.x[k], z = t.z[k], y = yAt(x, z);
      if (!(y === y)) continue;
      const ap = t.appear[k], spi = lut && ap < 255 ? lut[ap] : major, form = sp[spi].form;
      const cfg = LIGHT.FORM_CFG[form] || LIGHT.FORM_CFG.conifer, cr = LIGHT.CROWN[form] || LIGHT.CROWN.conifer;
      const rad = t.r[k] * 0.2;                                 // CPK r byte = r / 0.2 m (0: an unmeasured stand)
      let s = t.h[k] / 8.0;
      if (!(rad > 0)) s *= 0.45 + 0.55 * clamp((tl - y) / 140.0, 0.0, 1.0);
      s *= cfg[1];
      const wf = rad > 0 ? clamp(2.35 * rad / Math.max(1.0, s), 0.28, 1.15) : cfg[0];
      push(x, z, y, s, s * cr[1], 0.5 * LIGHT.SILHOUETTE * s * wf, cr[0] === 'dome', CLS_OF.get(cr[2]));
    }
  }
  for (const t of shrubs) {
    for (let k = 0; k < t.n; k++) {
      const x = t.x[k], z = t.z[k], y = yAt(x, z), hh = t.h[k] / 50.0;
      if (!(y === y)) continue;
      push(x, z, y, hh, hh * LIGHT.SHRUB[1], LIGHT.SHRUB_R * hh, LIGHT.SHRUB[0] === 'dome', CLS_OF.get(LIGHT.SHRUB[2]));
    }
  }
  return D;
}
// bake_light's object march steps at res: [ox, oy, thr f32] (world: {sun})
export function marchSteps(world, res) {
  const { dx, dy, tan } = world.sun, reach = Math.min(LIGHT.REACH_MAX_M, LIGHT.OBJ_MAX_M / Math.max(tan, 1e-3));
  const ds = [];
  for (let k = 1; res * k < Math.min(8.0, reach); k++) ds.push(res * k);
  for (let d = 8.0, k = 0; d < reach; k++, d = 8.0 + k * 2 * res) ds.push(d);
  const out = [], seen = new Set();
  for (const d of ds) {
    const ox = roundEven(dx * d / res), oy = roundEven(dy * d / res), key = ox + ',' + oy;
    if (seen.has(key) || (ox === 0 && oy === 0)) continue;
    seen.add(key);
    out.push([ox, oy, f32(d * tan + LIGHT.EPS_M)]);
  }
  return { steps: out, reach };
}
// box means (radius r cells, clipped to the grid) of a float raster at the cells [ja, jb) x
// [la, lb): separable float64 sums in a fixed per-cell order (window-independent: two slots'
// overlapping windows give the same bits)
function boxMeans(a, nx, nz, r, ja, jb, la, lb) {
  const out = new Float32Array(nx * nz), ra = Math.max(0, la - r), rb = Math.min(nz, lb + r), w = jb - ja;
  const S = new Float64Array((rb - ra) * w);
  for (let k = ra; k < rb; k++) {
    for (let i = ja; i < jb; i++) {
      let s = 0;
      for (let ii = Math.max(0, i - r); ii <= Math.min(nx - 1, i + r); ii++) s += a[k * nx + ii];
      S[(k - ra) * w + i - ja] = s;
    }
  }
  for (let k = la; k < lb; k++) {
    const k0 = Math.max(0, k - r), k1 = Math.min(nz - 1, k + r);
    for (let i = ja; i < jb; i++) {
      let s = 0;
      for (let kk = k0; kk <= k1; kk++) s += S[(kk - ra) * w + i - ja];
      out[k * nx + i] = f32(s / ((k1 - k0 + 1) * (Math.min(nx - 1, i + r) - Math.max(0, i - r) + 1)));
    }
  }
  return out;
}
// the crown factors on the world-aligned grid of `cell` m over world rect [xa, xb) x [za, zb)
// (the slot; the window adds the sun reach) -> {X0, Z0, nx, nz, cell, fsun, fsky, vo, c, ov, obj}
export function crownGrid(world, j, xa, xb, za, zb, cell) {
  const { steps, reach } = marchSteps(world, cell), halo = Math.ceil(reach / cell) + 3;
  const ia = Math.max(0, Math.floor(xa / cell) - halo), ib = Math.min(Math.ceil(world.w / cell), Math.ceil(xb / cell) + halo);
  const ka = Math.max(0, Math.floor(za / cell) - halo), kb = Math.min(Math.ceil(world.h / cell), Math.ceil(zb / cell) + halo);
  const nx = ib - ia, nz = kb - ka, X0 = ia * cell, Z0 = ka * cell;
  const xs = Float32Array.from({ length: nx }, (_, i) => X0 + (i + 0.5) * cell), zs = Float32Array.from({ length: nz }, (_, k) => Z0 + (k + 0.5) * cell);
  const g = tinRaster(world, j, xs, zs).h;
  // the discs: every cell centre inside the drawn radius, the tallest top (bake's key: 1/256 m, then cls)
  const D = crownDiscs(world, j), key = new Float64Array(nx * nz).fill(-1);
  for (let d = 0; d < D.x.length; d++) {
    const R = D.r[d], x = D.x[d], z = D.z[d], kk = Math.ceil(R / cell) + 1;
    const ci = Math.floor(x / cell) - ia, ck = Math.floor(z / cell) - ka;
    if (ci + kk < 0 || ci - kk >= nx || ck + kk < 0 || ck - kk >= nz) continue;
    const top = D.y[d] + D.top[d], base = D.y[d] + D.base[d];
    for (let b = -kk; b <= kk; b++) {
      const k = ck + b;
      if (k < 0 || k >= nz) continue;
      for (let a = -kk; a <= kk; a++) {
        const i = ci + a;
        if (i < 0 || i >= nx) continue;
        const q = Math.hypot((ia + i + 0.5) * cell - x, (ka + k + 0.5) * cell - z) / Math.max(R, 1e-6);
        if (!(q < 1.0)) continue;
        const zc = D.dome[d] ? 0.5 * (top + base) + 0.5 * (top - base) * Math.sqrt(1.0 - q * q) : top - (top - base) * q;
        const v = Math.floor((zc + 1000.0) * 256.0) * 16 + D.cls[d];
        if (v > key[k * nx + i]) key[k * nx + i] = v;
      }
    }
  }
  const obj = new Float32Array(nx * nz), cls = new Uint8Array(nx * nz), Sf = new Float32Array(nx * nz);
  for (let q = 0; q < nx * nz; q++) {
    if (key[q] >= 0) {
      const top = f32(Math.floor(key[q] / 16) / 256.0 - 1000.0), hgt = f32(top - g[q]);
      if (hgt >= LIGHT.CASTER_MIN_M) { obj[q] = Math.min(hgt, LIGHT.OBJ_MAX_M); cls[q] = key[q] % 16; }
    }
    Sf[q] = f32(obj[q] + g[q]);
  }
  // the sun march (crowns only: the terrain and the opaque casters are in SHD2), inner cells
  const ja = Math.max(0, Math.floor(xa / cell) - ia - 3), jb = Math.min(nx, Math.ceil(xb / cell) - ia + 3);
  const la = Math.max(0, Math.floor(za / cell) - ka - 3), lb = Math.min(nz, Math.ceil(zb / cell) - ka + 3);
  const vo = new Float32Array(nx * nz).fill(1);
  for (let k = la; k < lb; k++) {
    for (let i = ja; i < jb; i++) {
      const z0 = g[k * nx + i];
      let op = 0;
      for (const [ox, oy, thr] of steps) {
        const ii = i + ox, kk = k + oy;
        if (ii < 0 || ii >= nx || kk < 0 || kk >= nz) continue;
        const q = kk * nx + ii;
        if (cls[q] >= 2 && f32(Sf[q] - z0) > thr && OPACITY[cls[q]] > op) op = OPACITY[cls[q]];
      }
      vo[k * nx + i] = f32(1.0 - op);
    }
  }
  // the 3 x 3 box needs its ring marched; at the world's edge the box clamps (the bake's edge-clamped mean)
  const ba = ja ? ja + 1 : 0, bb = jb < nx ? jb - 1 : nx, ka2 = la ? la + 1 : 0, kb2 = lb < nz ? lb - 1 : nz;
  const vob = boxMeans(vo, nx, nz, 1, ba, bb, ka2, kb2);
  // the crowns' sky: box occupancy at LIGHT_SKY_FINE_M
  const occ = Float32Array.from(obj, (v) => (v > 0 ? 1 : 0)), ov = new Float32Array(nx * nz).fill(1);
  for (const r_m of LIGHT.SKY_FINE_M) {
    const r = Math.max(1, roundEven(r_m / cell)), kr = f32(LIGHT.OPEN_K * r_m), m = boxMeans(obj, nx, nz, r, ja, jb, la, lb), f = boxMeans(occ, nx, nz, r, ja, jb, la, lb);
    for (let k = la; k < lb; k++) for (let i = ja; i < jb; i++) {
      const q = k * nx + i;
      const m2 = f32(m[q] * m[q]), fk = f32(f[q] * kr), v = f32(1 - f32(f[q] * f32(m2 / f32(f32(f32(fk * fk) + m2) + f32(1e-6)))));
      if (v < ov[q]) ov[q] = v;
    }
  }
  const fsun = new Float32Array(nx * nz), fsky = new Float32Array(nx * nz), c = new Float32Array(nx * nz);
  for (let k = la; k < lb; k++) for (let i = ja; i < jb; i++) {
    const q = k * nx + i;
    c[q] = cls[q] >= 2 ? f32(clamp(f32(f32(obj[q] - LIGHT.CANOPY_M[0]) / f32(LIGHT.CANOPY_M[1] - LIGHT.CANOPY_M[0])), 0, 1) * LIGHT.CANOPY_TOP) : 0;
    fsun[q] = f32(vob[q] + f32(f32(1 - vob[q]) * c[q]));
    fsky[q] = f32(ov[q] + f32(f32(1 - ov[q]) * c[q]));
  }
  return { X0, Z0, nx, nz, cell, fsun, fsky, vo: vob, c, ov, obj, inner: [ba, bb, ka2, kb2] };
}
// a crown-grid field at one texel: bilinear (texel < cell) or the mean of the texel's cells
function gridAt(G, a, x, z, texel) {
  if (texel < G.cell) return bilin(a, G.nx, G.nz, G.cell, x - G.X0, z - G.Z0);
  const n = Math.round(texel / G.cell), i0 = Math.round((x - texel / 2 - G.X0) / G.cell), k0 = Math.round((z - texel / 2 - G.Z0) / G.cell);
  let s = 0;
  for (let k = 0; k < n; k++) for (let i = 0; i < n; i++) s += a[clamp(k0 + k, 0, G.nz - 1) * G.nx + clamp(i0 + i, 0, G.nx - 1)];
  return f32(s / (n * n));
}

// ---------------------------------------------------------------- 5. light
function shd2Level(levels, texel) {           // the coarsest level whose cell <= the texel, else the finest
  let L = levels[0];
  for (const l of levels) if (l.cell <= texel + 1e-9) L = l;
  return L;
}
// the rock field (C7, PROTOCOL "Rock field"): codes -> per 32 m cell (b / L in the game frame,
// x east y up z south; frac(b . (Xc, 0, Zc) / L) at the cell centre) — groundgen uploads F as is.
// Cells 0 0 0 = no strata (F 0). FAILS on an unknown ver. -> null when the tier measured none
export const STRATA_RAMP_DEG = 2.5;
export function strataField(meta, img, ids) {
  if (!meta || !meta.file || !img) return null;
  if (meta.ver !== 1) throw new Error(`rockfield ver ${meta.ver}: this client reads ver 1`);
  const { w, h, cell_m: cell, enc } = meta, F = new Float32Array(w * h * 4), T = meta.texels || {};
  const lin = (v, e) => e.lo + (v - 1) * (e.hi - e.lo) / 254;
  let cells = 0;
  for (let q = 0; q < w * h; q++) {
    const r = img[3 * q], g = img[3 * q + 1], b = img[3 * q + 2];
    if (!r && !g && !b) continue;
    const L = enc.r.log2 ? 2 ** lin(r, enc.r) : lin(r, enc.r), st = (g - 1) * enc.g.wrap / 254, dip = lin(b, enc.b);
    const dd = (st + 90) * Math.PI / 180, d = dip * Math.PI / 180;
    const mx = Math.sin(d) * Math.sin(dd) / L, my = Math.cos(d) / L, mz = -Math.sin(d) * Math.cos(dd) / L;
    const ph = mx * (q % w + 0.5) * cell + mz * (Math.floor(q / w) + 0.5) * cell;
    F[4 * q] = f32(mx); F[4 * q + 1] = f32(my); F[4 * q + 2] = f32(mz); F[4 * q + 3] = f32(ph - Math.floor(ph));
    cells++;
  }
  const set = (ks) => new Set((ks || []).map((k) => ids[k]).filter((v) => v !== undefined));
  const cd = T.cliff_deg ?? 90, rad = Math.PI / 180;
  return { F, w, h, cell, cells, rock: set(T.rock), cliff: set(T.cliff),
    t0: f32(Math.tan((cd - STRATA_RAMP_DEG) * rad)), t1: f32(Math.tan(Math.min(89.9, cd + STRATA_RAMP_DEG) * rad)) };
}
// the strata class weight: the bilinear of the 1 m rock indicator (rock 1, cliff smooth(slope))
function strataW(world, S, X, Z, slope) {
  const cm = world.cm, Wc = world.w, Hc = world.h, ramp = smooth32(slope, S.t0, S.t1);
  const u = f32(X - 0.5), v = f32(Z - 0.5), i = Math.floor(u), k = Math.floor(v), tu = f32(u - i), tv = f32(v - k);
  const at = (cx, cz) => { const c = cm[(clamp(cz, 0, Hc - 1) - world.oz) * world.ow + clamp(cx, 0, Wc - 1) - world.ox]; return S.rock.has(c) ? 1 : S.cliff.has(c) ? ramp : 0; };
  const a = f32(f32(at(i, k) * f32(1 - tu)) + f32(at(i + 1, k) * tu)), b = f32(f32(at(i, k + 1) * f32(1 - tu)) + f32(at(i + 1, k + 1) * tu));
  return f32(f32(a * f32(1 - tv)) + f32(b * tv));
}
// the tilt sum(wc keep sin(2 pi s) (b - (b . n) n)) at (X, h, Z), n = (nx, ny, nz) unit -> [px, py, pz]
function strataTilt(S, X, Z, h, gx, gz, nx, ny, nz, texel) {
  const u = X / S.cell - 0.5, v = Z / S.cell - 0.5, i0 = Math.floor(u), k0 = Math.floor(v);
  const tu = clamp(u - i0, 0, 1), tv = clamp(v - k0, 0, 1), P = [0, 0, 0];
  for (let c = 0; c < 4; c++) {
    const ci = clamp(i0 + (c & 1), 0, S.w - 1), ck = clamp(k0 + (c >> 1), 0, S.h - 1), o = 4 * (ck * S.w + ci);
    const mx = S.F[o], my = S.F[o + 1], mz = S.F[o + 2];
    if (mx === 0 && my === 0 && mz === 0) continue;
    const wc = ((c & 1) ? tu : 1 - tu) * ((c >> 1) ? tv : 1 - tv);
    if (!(wc > 0)) continue;
    const gpx = mx + my * gx, gpz = mz + my * gz, keep = octaveKeep(1 / Math.max(Math.hypot(gpx, gpz), 1e-6), texel);
    if (!(keep > 0)) continue;
    const s = S.F[o + 3] + mx * (X - (ci + 0.5) * S.cell) + my * h + mz * (Z - (ck + 0.5) * S.cell);
    const a = wc * keep * Math.sin(2 * Math.PI * (s - Math.floor(s))), im = 1 / Math.hypot(mx, my, mz);
    const bx = mx * im, by = my * im, bz = mz * im, d = bx * nx + by * ny + bz * nz;
    P[0] += a * (bx - d * nx); P[1] += a * (by - d * ny); P[2] += a * (bz - d * nz);
  }
  return P;
}
export function lightIrradiance(world, sun, sky, ny) {
  const L = world.light, w = f32(f32(0.5 * ny) + 0.5), E = [0, 0, 0];
  for (let c = 0; c < 3; c++) {
    const sc = f32(L.sky[c]), gc = f32(L.ground[c]);
    E[c] = clamp(f32(f32(f32(f32(f32(w * f32(sc - gc)) + gc) * sky) + f32(sun * f32(L.sun[c]))) * f32(1.0 / L.white)), 0, 1);
  }
  return E;
}

// ---------------------------------------------------------------- paint
export function paintRect(world, j, x0, z0, wM, hM, pw, ph, opts = {}) {
  const texel = wM / pw, tz = hM / ph, m = haloPx(texel), PW = pw + 2 * m, PH = ph + 2 * m;
  const xs = new Float32Array(PW), zs = new Float32Array(PH);
  for (let k = 0; k < PW; k++) xs[k] = f32(f32(x0) + f32((k - m + 0.5) * f32(texel)));
  for (let k = 0; k < PH; k++) zs[k] = f32(f32(z0) + f32((k - m + 0.5) * f32(tz)));
  const Wc = world.w, Hc = world.h, cm = world.cm;
  // 0. heights (the haloed raster)
  const H = opts.heights === 'dtm' ? dtmRaster(world, xs, zs) : tinRaster(world, j, xs, zs);
  // nearest class (paint's cm_win: trunc, clipped)
  const cmw = new Uint8Array(PW * PH), gxi = new Int32Array(PW), gzi = new Int32Array(PH);
  for (let i = 0; i < PW; i++) gxi[i] = clamp(Math.trunc(xs[i]), 0, Wc - 1);
  for (let k = 0; k < PH; k++) gzi[k] = clamp(Math.trunc(zs[k]), 0, Hc - 1);
  const ox = world.ox, oz = world.oz, ow = world.ow;
  for (let k = 0; k < PH; k++) for (let i = 0; i < PW; i++) cmw[k * PW + i] = cm[(gzi[k] - oz) * ow + gxi[i] - ox];
  // 1. the class blend
  const r = Math.max(1, roundEven(2.0 / texel)), out = new Float32Array(PW * PH * 3), lat = new Map();
  const sw = j > WAYS_MAX_J && !opts.noSealedWin;   // SEALED-WIN: this level reads no 1 m class / ways
  const wacc = new Float64Array(256), seen = new Uint8Array(256), ids = [];
  const F = { texel, elev: 0, slope: 0, patch: 0, mid: 0 }, Fs = new Array(PW * PH);
  for (let k = 0; k < PH; k++) {
    for (let i = 0; i < PW; i++) {
      const q = k * PW + i, X = xs[i], Z = zs[k];
      F.elev = H.h[q]; F.slope = f32(Math.sqrt(f32(f32(H.gz[q] * H.gz[q]) + f32(H.gx[q] * H.gx[q]))));
      F.patch = patchAt(X, Z, texel, lat); F.mid = fbm(X, Z, [9.5, 4.3], 23, texel);
      Fs[q] = [F.patch, F.mid];
      if (sw && world.sealed[cmw[q]]) {
        const col = classAlbedo(world, cmw[q], X, Z, F, opts);
        out[3 * q] = col[0]; out[3 * q + 1] = col[1]; out[3 * q + 2] = col[2];
        continue;
      }
      ids.length = 0;
      const i0 = Math.max(0, i - r), i1 = Math.min(PW - 1, i + r), k0 = Math.max(0, k - r), k1 = Math.min(PH - 1, k + r);
      const add = (id, n) => { if (!seen[id]) { seen[id] = 1; ids.push(id); } wacc[id] += n; };
      if (texel < 1.0) {
        // the 1 m cells under the box [xs[i0] - t/2, xs[i1] + t/2]: texel counts per cell (exact)
        const ax = xs[i0] - texel / 2, bx = xs[i1] + texel / 2, az = zs[k0] - tz / 2, bz = zs[k1] + tz / 2;
        for (let cz = Math.floor(az); cz < bz; cz++) {
          const nz = Math.round((Math.min(bz, cz + 1) - Math.max(az, cz)) / tz);
          if (nz <= 0) continue;
          const row = (clamp(cz, 0, Hc - 1) - oz) * ow - ox;
          for (let cx = Math.floor(ax); cx < bx; cx++) {
            const n = Math.round((Math.min(bx, cx + 1) - Math.max(ax, cx)) / texel);
            if (n > 0) add(cm[row + clamp(cx, 0, Wc - 1)], n * nz);
          }
        }
      } else {
        for (let kk = k0; kk <= k1; kk++) for (let ii = i0; ii <= i1; ii++) add(cmw[kk * PW + ii], 1);
      }
      ids.sort((a, b) => a - b);
      const tot = (i1 - i0 + 1) * (k1 - k0 + 1);
      let o0 = 0, o1 = 0, o2 = 0, ws = 0;
      for (const id of ids) {
        const w = f32(wacc[id] / tot), col = classAlbedo(world, id, X, Z, F, opts);
        o0 = f32(o0 + f32(w * col[0])); o1 = f32(o1 + f32(w * col[1])); o2 = f32(o2 + f32(w * col[2]));
        ws = f32(ws + w);
        wacc[id] = 0; seen[id] = 0;
      }
      const d = f32(Math.max(ws, f32(1e-6)));
      out[3 * q] = f32(o0 / d); out[3 * q + 1] = f32(o1 / d); out[3 * q + 2] = f32(o2 / d);
    }
  }
  // 2. the ways
  if (world.ways) waysPassRaster(world.ways, x0, z0, wM, hM, pw, ph, out, (i, k) => cmw[k * PW + i], (i, k) => H.h[k * PW + i]);
  // 3. the apron
  const inX = (i) => xs[i] >= 0 && xs[i] < Wc, inZ = (k) => zs[k] >= 0 && zs[k] < Hc;
  let allIn = true;
  for (let i = 0; i < PW && allIn; i++) allIn = inX(i);
  for (let k = 0; k < PH && allIn; k++) allIn = inZ(k);
  if (!allIn) {
    const ra = Math.max(1, roundEven(6.0 / texel)), cx = new Int32Array(PW + 1), cz = new Int32Array(PH + 1);
    for (let i = 0; i < PW; i++) cx[i + 1] = cx[i] + (inX(i) ? 1 : 0);
    for (let k = 0; k < PH; k++) cz[k + 1] = cz[k] + (inZ(k) ? 1 : 0);
    for (let k = 0; k < PH; k++) {
      const k0 = Math.max(0, k - ra), k1 = Math.min(PH, k + ra + 1);
      for (let i = 0; i < PW; i++) {
        const i0 = Math.max(0, i - ra), i1 = Math.min(PW, i + ra + 1), tot = (k1 - k0) * (i1 - i0);
        const wof = f32(clamp(f32((tot - (cz[k1] - cz[k0]) * (cx[i1] - cx[i0])) / tot), 0, 1));
        if (!(wof > 0)) continue;
        const q = k * PW + i, zc = zoneAlbedo(world, { elev: H.h[q], patch: Fs[q][0], mid: Fs[q][1] }), u = f32(1 - wof);
        for (let c = 0; c < 3; c++) out[3 * q + c] = f32(f32(out[3 * q + c] * u) + f32(zc[c] * wof));
      }
    }
  }
  // crop + clip -> the albedo (paint()'s return)
  const N = pw * ph, albedo = new Float32Array(N * 3), elev = new Float32Array(N);
  for (let k = 0; k < ph; k++) {
    for (let i = 0; i < pw; i++) {
      const s = (k + m) * PW + i + m, d = k * pw + i;
      for (let c = 0; c < 3; c++) albedo[3 * d + c] = clamp(out[3 * s + c], 0, 255);
      elev[d] = H.h[s];
    }
  }
  // 4. + 5. the light
  const sun = world.sun, Lv = shd2Level(world.shd2.vis, texel), Ls = shd2Level(world.shd2.sky, texel);
  const cell = opts.crownCell || (texel <= 1.0 ? 0.5 : texel);
  const G = opts.noCrowns ? null : crownGrid(world, j, x0, x0 + wM, z0, z0 + hM, cell);
  const E = new Uint8Array(N * 3), lit = new Uint8Array(N * 3), rgba = new Uint8Array(N * 4);
  const fsunA = new Float32Array(N), fskyA = new Float32Array(N), strA = new Float32Array(N);
  const SF = !opts.noStrata && world.strataK > 0 ? world.strata : null;
  for (let k = 0; k < ph; k++) {
    for (let i = 0; i < pw; i++) {
      const s = (k + m) * PW + i + m, d = k * pw + i, X = xs[i + m], Z = zs[k + m];
      let lam, ny;
      if (cmw[s] === world.waterId) { lam = f32(sun.sz); ny = 1; } else {
        const gx = H.gx[s], gs = H.gz[s], n = f32(Math.sqrt(f32(f32(f32(gx * gx) + f32(gs * gs)) + 1)));
        lam = clamp(f32(f32(f32(f32(gx * f32(-sun.sx)) + f32(gs * f32(sun.sy))) + f32(sun.sz)) / n), 0, 1);
        ny = f32(1 / n);
        const wr = SF ? strataW(world, SF, X, Z, f32(Math.sqrt(f32(f32(gx * gx) + f32(gs * gs))))) : 0;
        if (wr > 0) {
          const P = strataTilt(SF, X, Z, H.h[s], gx, gs, -gx / n, 1 / n, -gs / n, texel), kw = world.strataK * wr;
          const qx = -gx / n + kw * P[0], qy = 1 / n + kw * P[1], qz = -gs / n + kw * P[2], ql = Math.hypot(qx, qy, qz);
          strA[d] = f32(kw * Math.hypot(P[0], P[1], P[2]));
          if (strA[d] > 0) {
            lam = clamp(f32((qx * sun.sx + qy * sun.sz - qz * sun.sy) / ql), 0, 1);
            ny = f32(qy / ql);
          }
        }
      }
      const vis = bilin(Lv.a, Lv.w, Lv.h, Lv.cell, X, Z, 1 / 255, Lv.win), sky = bilin(Ls.a, Ls.w, Ls.h, Ls.cell, X, Z, 1 / 255, Ls.win);
      const fs = G ? gridAt(G, G.fsun, X, Z, texel) : 1, fk = G ? gridAt(G, G.fsky, X, Z, texel) : 1;
      fsunA[d] = fs; fskyA[d] = fk;
      const e = lightIrradiance(world, f32(f32(lam * vis) * fs), f32(sky * fk), ny);
      for (let c = 0; c < 3; c++) {
        const e8 = linToSrgb8(e[c]);
        E[3 * d + c] = e8;
        lit[3 * d + c] = linToSrgb8(f32(srgbToLin32(f32(albedo[3 * d + c] / f32(255))) * LUT8[e8]));
      }
    }
  }
  // GmTex orientation: row 0 = the SOUTH edge
  for (let k = 0; k < ph; k++) {
    for (let i = 0; i < pw; i++) {
      const s = k * pw + i, d = (ph - 1 - k) * pw + i;
      rgba[4 * d] = lit[3 * s]; rgba[4 * d + 1] = lit[3 * s + 1]; rgba[4 * d + 2] = lit[3 * s + 2]; rgba[4 * d + 3] = 255;
    }
  }
  return { rgba, albedo, E, lit, fsun: fsunA, fsky: fskyA, strata: strA, elev, texel, crown: G };
}
export function paintSlot(world, j, tx, ty, opts = {}) {
  const m = nodeSide(j);
  return paintRect(world, j, tx * m, ty * m, m, m, SLOT_PX, SLOT_PX, opts).rgba;
}
